import { supabase } from '../supabaseClient';

const DEFAULT_DEV_WORKER_URL = 'https://ghost-r2-dev.johnpirrie.workers.dev';
const DEFAULT_PROD_WORKER_URL = 'https://ghost-r2-prod.johnpirrie.workers.dev';

const trimTrailingSlashes = (value = '') => String(value || '').replace(/\/+$/, '');

export const getAiWorkerBaseUrl = () => {
  const configured = String(process.env.REACT_APP_AI_WORKER_BASE || '').trim();
  if (configured) return trimTrailingSlashes(configured);

  const sharedWorker = String(process.env.REACT_APP_R2_WORKER_URL || '').trim();
  if (sharedWorker) return trimTrailingSlashes(sharedWorker);

  return process.env.NODE_ENV === 'production'
    ? DEFAULT_PROD_WORKER_URL
    : DEFAULT_DEV_WORKER_URL;
};

let _getAiSessionToken = async () => {
  const { data } = await supabase.auth.getSession();
  const token = data?.session?.access_token;
  if (!token) {
    throw new Error('Unable to retrieve auth token for AI worker');
  }
  return token;
};

export const getAiSessionToken = () => _getAiSessionToken();
export const __setAiSessionTokenProvider = (fn) => {
  _getAiSessionToken = fn;
};

let _requestAiWorker = async (path, options = {}) => {
  const base = getAiWorkerBaseUrl();
  if (!base) throw new Error('Unable to determine AI worker base URL.');

  const normalizedPath = String(path || '').startsWith('/') ? String(path || '') : `/${String(path || '')}`;
  const url = `${base}${normalizedPath}`;

  let token = await _getAiSessionToken();
  const sendRequest = async (accessToken) => {
    const headers = {
      ...(options.headers || {}),
      Authorization: `Bearer ${accessToken}`
    };
    return fetch(url, { ...options, headers, signal: options.signal });
  };

  let response = await sendRequest(token);
  if (response && response.status === 401) {
    const { data: refreshData } = await supabase.auth.refreshSession();
    const refreshedToken = refreshData?.session?.access_token;
    if (refreshedToken && refreshedToken !== token) {
      token = refreshedToken;
      response = await sendRequest(token);
    }
  }

  return response;
};

export const requestAiWorker = (path, options = {}) => _requestAiWorker(path, options);
export const __setRequestAiWorker = (fn) => {
  _requestAiWorker = fn;
};

let _openRouterModelsCache = null;
let _openRouterModelsInFlight = null;
let _openRouterModelsInFlightStartedAt = 0;
const OPENROUTER_MODELS_CACHE_MS = 5 * 60 * 1000;
const OPENROUTER_MODELS_INFLIGHT_STALE_MS = 20 * 1000;
const OPENROUTER_MODELS_TIMEOUT_MS = 20 * 1000;

const parseErrorPayload = async (response) => {
  let parsed = null;
  try {
    parsed = await response.json();
  } catch {
    parsed = null;
  }

  if (response.ok) return parsed;

  const message = parsed?.error || `AI request failed (${response.status})`;
  const details = parsed?.details ? `: ${String(parsed.details)}` : '';
  const error = new Error(`${message}${details}`);
  error.status = Number(response.status || 0);
  error.details = parsed?.details || null;
  error.payload = parsed;
  throw error;
};

const normalizeAiTransportError = (error) => {
  const message = String(error?.message || '').trim();
  const normalizedName = String(error?.name || '').trim().toLowerCase();
  const isAbort = normalizedName === 'aborterror';
  const isNetworkFetch = /failed to fetch|networkerror|load failed|network request failed/i.test(message)
    || (String(error) === 'TypeError: Failed to fetch');

  if (!isAbort && !isNetworkFetch) {
    return error;
  }

  const wrapped = new Error(isAbort
    ? 'AI request was cancelled before completion.'
    : 'Unable to reach AI service. Check your internet connection or AI worker availability and try again.');
  wrapped.code = isAbort ? 'AI_REQUEST_ABORTED' : 'AI_FETCH_FAILED';
  wrapped.status = Number(error?.status || 0);
  wrapped.cause = error;
  return wrapped;
};

const extractSseDeltaText = (payload) => {
  const choice = Array.isArray(payload?.choices) ? payload.choices[0] : null;
  const delta = choice?.delta;
  if (!delta) return '';

  if (typeof delta.content === 'string') {
    return delta.content;
  }

  if (Array.isArray(delta.content)) {
    return delta.content
      .map((part) => (typeof part?.text === 'string' ? part.text : ''))
      .join('');
  }

  return '';
};

const readOpenRouterSse = async ({ response, onDelta } = {}) => {
  if (!response?.body) {
    throw new Error('AI stream did not include a readable body.');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let text = '';
  let lastPayload = null;

  const processLine = (line = '') => {
    const trimmed = String(line || '').trim();
    if (!trimmed.startsWith('data:')) return;

    const data = trimmed.slice(5).trim();
    if (!data || data === '[DONE]') return;

    let payload = null;
    try {
      payload = JSON.parse(data);
    } catch {
      return;
    }

    lastPayload = payload;
    const delta = extractSseDeltaText(payload);
    if (!delta) return;

    text += delta;
    if (typeof onDelta === 'function') {
      onDelta(text, delta, payload);
    }
  };

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';
    lines.forEach(processLine);
  }

  buffer += decoder.decode();
  if (buffer.trim()) processLine(buffer);

  return {
    text,
    raw: lastPayload
  };
};

export async function apiAiChatCompletions({
  model,
  messages,
  temperature,
  top_p,
  max_tokens,
  stream = false,
  response_format,
  provider,
  tool_choice,
  tools,
  signal
} = {}) {
  if (!Array.isArray(messages) || messages.length === 0) {
    throw new Error('apiAiChatCompletions requires a non-empty messages array.');
  }

  const payload = {
    model,
    messages,
    temperature,
    top_p,
    max_tokens,
    stream,
    response_format,
    provider,
    tool_choice,
    tools
  };

  const body = Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== undefined)
  );

  let response = null;
  try {
    response = await requestAiWorker('/v1/ai/openrouter/chat/completions', {
      method: 'POST',
      headers: {
        'content-type': 'application/json'
      },
      body: JSON.stringify(body),
      signal
    });
  } catch (error) {
    throw normalizeAiTransportError(error);
  }
  const parsed = await parseErrorPayload(response);
  return parsed;
}

export async function apiAiChatCompletionsStream({
  model,
  messages,
  temperature,
  top_p,
  max_tokens,
  response_format,
  provider,
  tool_choice,
  tools,
  signal,
  onDelta
} = {}) {
  if (!Array.isArray(messages) || messages.length === 0) {
    throw new Error('apiAiChatCompletionsStream requires a non-empty messages array.');
  }

  const payload = {
    model,
    messages,
    temperature,
    top_p,
    max_tokens,
    stream: true,
    response_format,
    provider,
    tool_choice,
    tools
  };

  const body = Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== undefined)
  );

  let response = null;
  try {
    response = await requestAiWorker('/v1/ai/openrouter/chat/completions', {
      method: 'POST',
      headers: {
        'content-type': 'application/json'
      },
      body: JSON.stringify(body),
      signal
    });
  } catch (error) {
    throw normalizeAiTransportError(error);
  }

  if (!response.ok) {
    await parseErrorPayload(response);
  }

  return readOpenRouterSse({ response, onDelta });
}

export async function apiAiListOpenRouterModels({ signal, forceRefresh = false } = {}) {
  const now = Date.now();
  if (!forceRefresh && _openRouterModelsCache && (now - _openRouterModelsCache.ts) < OPENROUTER_MODELS_CACHE_MS) {
    return _openRouterModelsCache.rows;
  }

  const inFlightIsFresh = _openRouterModelsInFlight
    && (now - _openRouterModelsInFlightStartedAt) < OPENROUTER_MODELS_INFLIGHT_STALE_MS;
  if (!forceRefresh && inFlightIsFresh) {
    return _openRouterModelsInFlight;
  }

  if (!inFlightIsFresh) {
    _openRouterModelsInFlight = null;
    _openRouterModelsInFlightStartedAt = 0;
  }

  const timeoutController = new AbortController();
  const timeoutId = setTimeout(() => timeoutController.abort(), OPENROUTER_MODELS_TIMEOUT_MS);
  const forwardedSignal = signal || timeoutController.signal;

  _openRouterModelsInFlight = (async () => {
  let response = null;
  try {
    response = await requestAiWorker('/v1/ai/openrouter/models', {
      method: 'GET',
      headers: {
        accept: 'application/json'
      },
      signal: forwardedSignal
    });
  } catch (error) {
    throw normalizeAiTransportError(error);
  } finally {
    clearTimeout(timeoutId);
  }

  let parsed = null;
  try {
    parsed = await response.json();
  } catch {
    parsed = null;
  }

  if (!response.ok) {
    const message = parsed?.error || `AI models request failed (${response.status})`;
    const details = parsed?.details ? `: ${String(parsed.details)}` : '';
    const error = new Error(`${message}${details}`);
    error.status = Number(response.status || 0);
    error.details = parsed?.details || null;
    error.payload = parsed;
    throw error;
  }

    const rows = Array.isArray(parsed?.data) ? parsed.data : [];
    const normalized = rows.map(row => ({
      id: String(row?.id || ''),
      name: String(row?.name || row?.id || ''),
      context_length: Number(row?.context_length || 0) || null,
      pricing: row?.pricing || null,
      raw: row
    }));

    _openRouterModelsCache = {
      ts: Date.now(),
      rows: normalized
    };

    return normalized;
  })();
  _openRouterModelsInFlightStartedAt = Date.now();

  try {
    return await _openRouterModelsInFlight;
  } finally {
    _openRouterModelsInFlight = null;
    _openRouterModelsInFlightStartedAt = 0;
  }
}
